import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import '../../styles/components/auth/terms_layout.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasReadCompletely, setHasReadCompletely] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  
  // Get registration data from navigation state
  const registrationData = location.state?.registrationData;
  const tenantId = location.state?.tenantId;
  const termsAccepted = location.state?.termsAccepted || false;

  // Handle scroll tracking
  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = (scrollTop / scrollHeight) * 100;
    
    setScrollProgress(progress);
    
    // Mark as read when user scrolls to at least 90%
    if (progress >= 90) {
      setHasReadCompletely(true);
    }
  };

  // Use window scroll event instead of element scroll
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleComplete = async () => {
    if (hasReadCompletely && registrationData) {
      try {
        setIsRegistering(true);
        
        // Add terms and privacy acceptance to registration data
        const finalRegistrationData = {
          ...registrationData,
          termsAccepted: true,
          privacyAccepted: true
        };
        
        console.log('Submitting registration data:', finalRegistrationData);
        
        // Register the user
        const response = await authService.register(finalRegistrationData);
        
        console.log('Registration response:', response);
        
        if (response.token) {
          localStorage.setItem('token', response.token);
        }
        
        // Navigate to email verification
        navigate('/verify-email', { 
          state: process.env.REACT_APP_ENABLE_MULTI_TENANT === 'true' && tenantId
            ? { email: registrationData.email, tenantId: tenantId }
            : { email: registrationData.email }
        });
        
      } catch (error) {
        console.error('Registration error:', error);
        console.error('Error details:', error.response?.data);
        
        // More specific error handling
        let errorMessage = 'Registration failed. Please try again.';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          if (Array.isArray(errors)) {
            errorMessage = errors.map(err => err.message || err).join(', ');
          } else if (typeof errors === 'object') {
            errorMessage = Object.values(errors).join(', ');
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(errorMessage);
        setIsRegistering(false);
      }
    }
  };

  const handleBack = () => {
    // Go back to Terms of Use
    navigate('/terms-of-use', {
      state: {
        registrationData: registrationData,
        tenantId: tenantId
      }
    });
  };

  return (
    <div className="terms-layout">
      <div className="back-button-top">
        <button onClick={handleBack} className="btn-back-top">
          ← Back to Terms
        </button>
      </div>
      
      <div className="terms-header">
        
        
        <h1>Privacy Policy</h1>
        <p>Please read our Privacy Policy carefully to understand how we protect your information</p>
        
        <div className="reading-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${scrollProgress}%` }}
            ></div>
          </div>
          <span className="progress-text">
            {Math.round(scrollProgress)}% read
          </span>
        </div>
      </div>

      <div className="terms-content-wrapper">
        <div className="terms-content">
          <div className="content-inner">
              <h2>1. Introduction</h2>
              <p>
                At Neurolex ("we," "us," or "our"), we are committed to protecting your privacy and the security of your personal and professional information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application (the "Mental Health Professional Portal") and related services designed for mental health professionals.
              </p>
              <p>
                We understand the sensitive nature of mental health data and have designed our privacy practices accordingly. By accessing the Mental Health Professional Portal, you consent to the data practices described in this Privacy Policy.
              </p>

              <h2>2. Information We Collect</h2>
              
              <h3 className="subsection-title">2.1 Information You Provide to Us</h3>
              <p>We collect information you voluntarily provide when registering and using the Mental Health Professional Portal, including:</p>
              
              <h4>Account Information:</h4>
              <ul>
                <li>Clinic affiliation</li>
                <li>First and last name</li>
                <li>Email address</li>
                <li>Password (stored securely in encrypted form)</li>
              </ul>

              <h4>Personal Information:</h4>
              <ul>
                <li>Personal contact number</li>
                <li>Clinic location</li>
                <li>Clinic contact number</li>
              </ul>

              <h4>Professional Information:</h4>
              <ul>
                <li>Specialty area</li>
                <li>Professional title</li>
                <li>Areas of expertise</li>
                <li>Years of experience</li>
              </ul>

              <h4>Availability:</h4>
              <ul>
                <li>Days available for appointments</li>
                <li>Specific hours available on each day</li>
                <li>Types of appointments offered</li>
              </ul>

              <h4>Credentials:</h4>
              <ul>
                <li>Education (degree, institution, year obtained)</li>
                <li>Licenses (type, license number, expiration date)</li>
                <li>Certifications (certification name, issuing authority, year obtained)</li>
                <li>Proof of credentials (copies of license documents, education certificates, and other relevant documents)</li>
              </ul>

              <h4>Public Profile Information:</h4>
              <ul>
                <li>Full professional name (including title)</li>
                <li>Specialty</li>
                <li>Professional description</li>
                <li>Personal contact information</li>
                <li>Availability schedule</li>
                <li>Credentials summary</li>
              </ul>

              <h4>Session Information:</h4>
              <ul>
                <li>Appointment scheduling details with patients</li>
                <li>Payment verification, including proof of payment submitted by patients</li>
              </ul>

              <h4>Patients' Journal Logs and Mood Check-In Logs:</h4>
              <p>Doctor's notes and observations incorporated within AI-powered analysis reports</p>

              <h3 className="subsection-title">2.2 Information Collected Automatically</h3>
              <p>When you use our Mental Health Professional Portal, we may automatically collect certain information about your device and how you interact with the platform, including:</p>
              <ul>
                <li>Device type, operating system, and browser type</li>
                <li>IP address and geographic location data</li>
                <li>Usage data such as features accessed, time spent on the portal, and navigation behavior</li>
                <li>Log data including login times, session durations, and error or crash reports</li>
              </ul>

              <h3 className="subsection-title">2.3 Information from Third Parties</h3>
              <p>
                If you choose to register or log in to the Mental Health Professional Portal using a Google account, we may receive certain information from Google in accordance with your privacy settings on that service. This may include your name, email address, and profile information necessary to facilitate your access and use of the Portal.
              </p>
              <p>
                After signing in with your Google account, you will be required to provide additional professional and personal details to complete your profile and enable full use of the Portal.
              </p>

              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              
              <h3 className="subsection-title">3.1 Support Your Professional Practice</h3>
              <ul>
                <li>Create and manage your professional account</li>
                <li>Facilitate scheduling and management of patient appointments</li>
                <li>Enable secure communication with patients, including video consultations and messaging</li>
                <li>Provide tools to review and analyze patient journal entries and mood data with AI-assisted insights</li>
                <li>Help you track patient progress through mood trends and emotional state visualizations</li>
              </ul>

              <h3 className="subsection-title">3.2 Enhance the Portal and Services</h3>
              <ul>
                <li>Analyze usage data to improve the functionality and user experience of the Mental Health Professional Portal</li>
                <li>Develop and refine AI tools and analytics to better support clinical decision-making</li>
                <li>Conduct anonymized research to advance understanding of mental health trends and treatment outcomes</li>
              </ul>

              <h2>4. How We Protect Your Information</h2>
              
              <h3 className="subsection-title">4.1 Security Measures</h3>
              <p>We prioritize the security of your personal and professional information by implementing multiple safeguards, including:</p>
              <ul>
                <li>Encrypting all sensitive data both during transmission and while stored</li>
                <li>Limiting access to your information strictly to authorized personnel</li>
                <li>Conducting regular security audits and penetration testing to identify and address vulnerabilities</li>
              </ul>

              <h3 className="subsection-title">4.2 Data Retention</h3>
              <p>
                We retain your personal and professional information only as long as necessary to fulfill the purposes described in this Privacy Policy, except when longer retention is mandated or permitted by law.
              </p>

              <h3 className="subsection-title">4.3 Data Storage</h3>
              <p>
                Your information is securely stored and processed on protected servers. Access to your data is strictly limited to authorized system administrators solely for service delivery and system maintenance.
              </p>

              <h2>5. Sharing Your Information</h2>
              
              <h3 className="subsection-title">5.1 With Your Consent</h3>
              <p>We only share your information with your explicit consent when necessary to facilitate your professional use of the Portal. This may include sharing:</p>
              <ul>
                <li>Your name and contact details</li>
                <li>Professional information relevant to your practice</li>
                <li>Appointment details and related communications</li>
              </ul>

              <h3 className="subsection-title">5.2 Service Providers</h3>
              <p>We may share information with trusted third-party vendors, consultants, and service providers who assist us in operating the Portal, such as:</p>
              <ul>
                <li>Cloud storage providers</li>
                <li>Email and communication service providers</li>
                <li>Analytics and technical support companies</li>
              </ul>
              <p>
                All such service providers are contractually required to protect your information and use it solely to support our services in compliance with this Privacy Policy.
              </p>

              <h3 className="subsection-title">5.3 Legal Requirements</h3>
              <p>We may disclose your information when legally required or when we believe disclosure is necessary to:</p>
              <ul>
                <li>Comply with laws, regulations, or legal processes</li>
                <li>Protect our rights, property, or safety, as well as that of users or the public</li>
                <li>Investigate or prevent fraud or misconduct</li>
              </ul>

              <h3 className="subsection-title">5.4 Business Changes</h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction. We will notify you of any such changes and continue to protect your information under this Privacy Policy.
              </p>

              <h2>6. Your Rights and Choices</h2>
              
              <h3 className="subsection-title">6.1 Account Information</h3>
              <p>You can access and update your professional profile and account information anytime through the Portal's User Profile section. This includes:</p>
              <ul>
                <li>Editing your personal and professional details</li>
                <li>Updating your password and email address</li>
                <li>Managing linked accounts, such as Google authentication</li>
              </ul>

              <h3 className="subsection-title">6.2 Communications</h3>
              <p>All communications regarding your account, appointments, and support will be sent securely through the Portal.</p>

              <h3 className="subsection-title">6.3 Data Access and Control</h3>
              <p>
                You have the right to view and update your personal and professional data at any time. If you wish to request a copy of your information or request deletion, please contact us at neurolex.life@gmail.com. Certain data may be retained as required by law or to ensure proper operation of services.
              </p>

              <h3 className="subsection-title">6.4 Do Not Track</h3>
              <p>
                Our Portal does not track your activity outside of the application and does not respond to "Do Not Track" browser signals. By using the Portal, you consent to your activity within the system being accessible to authorized system administrators solely for service support and maintenance purposes.
              </p>

              <h2>7. Special Notes About Mental Health Data</h2>
              <p>
                As a mental health professional, you understand the sensitive nature of the information you handle. While the data collected through the Portal may not always qualify as "protected health information" under HIPAA, we maintain strict confidentiality and security standards to protect all mental health information.
              </p>
              <p>
                The AI tools integrated into the Portal analyze patient journal entries and mood check-ins to provide insights, with all processing conducted in secure environments with limited access to ensure patient privacy and data protection.
              </p>

              <h2>8. Eligibility and Access</h2>
              <p>
                Access to the Mental Health Professional Portal is limited to licensed and verified professionals. All professionals must submit required credentials—such as proof of licensure, certifications, and education—for administrative review. Only upon approval by the system administrators will access be granted. We reserve the right to reject or revoke access if the submitted information is found to be incomplete, invalid, or no longer up to date.
              </p>

              <h2>9. Changes to This Privacy Policy</h2>
              <p>
                We may revise this Privacy Policy to reflect changes in legal requirements, platform functionality, or data handling practices. When updates occur, we will post a revised version with the "Last Updated" date clearly marked at the top of the policy.
              </p>
              <p>
                You will be notified of any significant changes through in-app notifications or by email. Continued use of the Portal after any changes indicates your acceptance of the updated policy. We encourage you to review this Privacy Policy periodically to stay informed about how your information is protected.
              </p>

              <h2>10. Contact Us</h2>
              <p>If you have any questions, concerns, or requests related to this Privacy Policy or how your information is managed as a mental health professional, you may contact us at:</p>
              <ul className="contact-list">
                <li className="contact-list-item"><strong>Phone:</strong> +63 966 893 6783</li>
                <li className="contact-list-item"><strong>Email:</strong> neurolex.life@gmail.com</li>
              </ul>

              <div className="terms-footer">
                <p>
                  <strong>By using the Neurolex Professional Portal, you confirm that you have read, understood, and agreed to the terms of this Privacy Policy.</strong>
                </p>
                <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
                <p><strong>Effective date:</strong> {new Date().toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="terms-actions">
          {hasReadCompletely ? (
            <div className="action-group">
              <p className="completion-message">
                ✓ You have read the complete Privacy Policy
              </p>
              <div className="button-group">
                <button onClick={handleBack} className="btn-back">
                  ← Back to Terms
                </button>
                <button onClick={handleComplete} className="btn-complete" disabled={isRegistering}>
                  {isRegistering ? 'Creating Account...' : 'Accept & Create Account'}
                </button>
              </div>
            </div>
          ) : (
            <div className="action-group">
              <p className="reading-instruction">
                Please scroll down to read the complete Privacy Policy
              </p>
              <div className="button-group">
                <button onClick={handleBack} className="btn-back">
                  ← Back to Terms
                </button>
                <button disabled className="btn-complete disabled">
                  Accept & Create Account
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default PrivacyPolicy;